import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextExpenseReport } from './ExpenseReport';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ExpenseReportDA } from '../../../../services/admin/expense.report.service';
import { ExpenseDA } from '../../../../services/admin/expense.service';
import { Expense } from '../../../../models/expense.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { ExpenseReport } from '../../../../models/expense.report.model';
import { colorStatusExpenseReport, colorStatusPayment } from '../../../../scripts/badge.color.script';
import { getError } from '../../../../languages/translations/response';
import { statusDocumentValue, normalizeStatus } from '../../../../libraries/status.document.library';
import { modalShow } from '../../../../utils/modal.util';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { MediaDA } from '../../../../services/admin/media.service';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatMoney, formatContact } from '../../../../scripts/format.value.script';
import { treasuryAccountSelected } from '../../../../scripts/list.selected.script';
import { paymentMethodValue, paymentStatusValue } from '../../../../libraries/payment.library';

import AppPurchaseViewDetail from '../component/PurchaseViewDetail';
import AppPurchaseViewAmount from '../component/PurchaseViewAmount';
import AppPreloader from '../../../../components/Preloader';
import AppPurchaseViewDraft from '../component/PurchaseViewDraft';
import DocumentNavigation from '../../../../components/element/DocumentNavigation';
import ButtonVouchers from '../../../../components/button/ButtonVouchers';
import ModalExpenseReportValidate from '../../../../components/modal/ModalExpenseReportValidate';
import ModalPaymentCreateExpenseReport from '../../../../components/modal/ModalPaymentCreateExpenseReport';

export interface ExpenseReportViewPageProps {};

let errorResponse: Error;

const ExpenseReportViewPage: React.FunctionComponent<ExpenseReportViewPageProps> = props => {
  const {asideMinimize, setAsideMinimize, setRoute, companyForUser, settingsForCompany, accountingAccountsForCompany, accountingTaxAccountsForCompany, accountingExpenseAccountsForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany, loadAccountingDebitEntriesForCompany} = UseOutletContextExpenseReport()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [report, setReport] = useState<ExpenseReport | null>(null)
  const [expenses, setExpenses] = useState<(Expense | null)[]>([])
  const [removePayment, setRemovePayment] = useState(false)
  const [vouchers, setVouchers] = useState({list: true, value: [] as {name: string, size: number, type: string, view: string, file: any}[], valid: [] as {name: boolean, file: boolean}[]})

  const computeExpenseTotal = (expense: Expense): number => {
    if (!expense.requirements?.length) return expense.amount?.total ?? 0
    let total = 0
    for (const req of expense.requirements) {
      for (const content of (req.contents ?? [])) {
        const price = Number(content.price_unit) || 0
        const qty = Number(content.quantity) || 1
        const lineAmount = price * qty
        if (content.tax_included) {
          total += lineAmount
        } else {
          const rate = content.tax_rate ?? 0
          total += lineAmount * (1 + rate)
        }
      }
    }
    return total || (expense.amount?.total ?? 0)
  }

  const computeGrandTotal = (): number => {
    return expenses.reduce((sum, exp) => sum + ((exp?.amount?.total ?? (exp ? computeExpenseTotal(exp) : 0))), 0)
  }

  const getExpenseForeignAmount = (expense: Expense): { amount: number; code: string } | null => {
    if (!expense.foreign_currency?.code || !companyForUser) return null
    if (expense.foreign_currency.code === companyForUser.currency?.code) return null
    const total = computeExpenseTotal(expense)
    return { amount: total, code: expense.foreign_currency.code }
  }

  const loadReport = async (id_company: string, id_report: string) => {
    try {
      const response = await ExpenseReportDA.getExpenseReport(id_company, id_report)
      if (response.status === 200) {
        const data = response.data
        data.status = normalizeStatus(data.status)
        if (data.payment_status) data.payment_status = normalizeStatus(data.payment_status)

        let tempVouchersValue: {name: string, size: number, type: string, view: string, file: any}[] = []
        let tempVouchersValid: {name: boolean, file: boolean}[] = []

        for (let voucher of (data.vouchers ?? [])) {
          const vName = voucher.original_name ?? voucher.name ?? ''
          const vType = voucher.content_type ?? 'application/pdf'
          let vView = ''
          if (voucher.id_document_inbox && companyForUser) {
            const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, voucher.id_document_inbox)
            if (signedUrl) vView = signedUrl
          } else if (voucher.file) {
            vView = voucher.file
          }
          tempVouchersValue.push({ name: vName, size: voucher.size ?? 0, type: vType, view: vView, file: vView })
          tempVouchersValid.push({ name: !!vName, file: !!vView })
        }

        setVouchers({list: true, value: tempVouchersValue, valid: tempVouchersValid})
        setReport(data)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const submitDelete = async () => {
    if (companyForUser && report) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisExpenseReport,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await ExpenseReportDA.deleteExpenseReport(companyForUser.id, report.id)
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then(() => {
              navigate('/main/purchase/expense-report/list', {replace: true})
            })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        }
      })
    }
  }

  const submitSubmit = async () => {
    if (companyForUser && report) {
      GlobalSwal.fire({
        title: lang.labels.youWantToSubmitThisExpenseReport,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await ExpenseReportDA.submitExpenseReport(companyForUser.id, report.id)
          if (response.status === 200) {
            const d = response.data; d.status = normalizeStatus(d.status); if (d.payment_status) d.payment_status = normalizeStatus(d.payment_status); setReport(d)
            GlobalSwal.fire({ title: lang.labels.expenseReportSubmittedSuccessfully, icon: 'success', showConfirmButton: false, timer: 2200 })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error', confirmButtonText: lang.labels.okGotIt })
          }
        }
      })
    }
  }

  const submitReject = async () => {
    if (companyForUser && report) {
      GlobalSwal.fire({
        title: lang.labels.rejectExpenseReport,
        input: 'textarea',
        inputLabel: lang.labels.rejectionReason,
        inputPlaceholder: lang.labels.rejectionReasonPlaceholder,
        inputValidator: (value: string) => {
          if (!value) return lang.labels.rejectionReasonRequired
        },
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result: any) => {
        if (result.isConfirmed && result.value) {
          const response = await ExpenseReportDA.rejectExpenseReport(companyForUser.id, report.id, result.value)
          if (response.status === 200) {
            const d = response.data; d.status = normalizeStatus(d.status); if (d.payment_status) d.payment_status = normalizeStatus(d.payment_status); setReport(d)
            GlobalSwal.fire({ title: lang.labels.successfullyUpdated, icon: 'success', showConfirmButton: false, timer: 2200 })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error', confirmButtonText: lang.labels.okGotIt })
          }
        }
      })
    }
  }

  const executeShowModalValidate = () => {
    if (report) {
      modalShow('modal-expense-report-validate')
    }
  }

  const submitInvalidate = async () => {
    if (companyForUser && report) {
      GlobalSwal.fire({
        title: lang.labels.youWantToInvalidateThisDocument ?? lang.labels.areYouSureYouWantToProceed,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await ExpenseReportDA.invalidateExpenseReport(companyForUser.id, report.id)
          if (response.status === 200) {
            const d = response.data; d.status = normalizeStatus(d.status); if (d.payment_status) d.payment_status = normalizeStatus(d.payment_status); setReport(d)
            GlobalSwal.fire({ title: lang.labels.successfullyUpdated, icon: 'success', showConfirmButton: false, timer: 2200 })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error', confirmButtonText: lang.labels.okGotIt })
          }
        }
      })
    }
  }

  const executeShowModalCorrectAccounts = () => {
    modalShow('modal-expense-report-correct-accounts')
  }

  const executeShowModalPayment = () => {
    modalShow('modal-payment-create-expense-report')
  }

  const excuteViewOrHideButtonsToPaymentDelete = () => {
    setRemovePayment(!removePayment)
  }

  const submitPaymentDelete = async (id_payment: string) => {
    if (companyForUser && report) {
      GlobalSwal.fire({
        title: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await ExpenseReportDA.deleteExpenseReportPayment(companyForUser.id, report.id, id_payment)
          if (response.status === 200) {
            const d = response.data; d.status = normalizeStatus(d.status); if (d.payment_status) d.payment_status = normalizeStatus(d.payment_status); setReport(d)
            GlobalSwal.fire({ title: lang.labels.successfullyUpdated, icon: 'success', showConfirmButton: false, timer: 2200 })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({ title: getError(errorResponse.code, lang.code, errorResponse.message), text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error', confirmButtonText: lang.labels.okGotIt })
          }
        }
      })
    }
  }

  useEffect(() => {
    if (companyForUser && param.id) {
      loadReport(companyForUser.id, param.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect(() => {
    if (companyForUser && report?.lines && report.lines.length > 0) {
      Promise.all(
        report.lines.map(line =>
          ExpenseDA.getExpense(companyForUser.id, line.id_expense)
            .then(r => r.status === 200 ? (r.data as Expense) : null)
            .catch(() => null)
        )
      ).then(results => setExpenses(results))
    } else {
      setExpenses([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, report])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.expenseReports, branch: lang.labels.viewExpenseReport}, company: false})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && report
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            <div className="card card-flush mb-5">
              <div className="card-header pt-3">
                <div className="card-title align-items-start flex-column">
                  <div className="d-flex align-items-center gap-3">
                    <h1 className="mb-0">{report.expense_report_number}</h1>
                    {companyForUser && param.id && <DocumentNavigation navKey="nav_expense_reports" currentId={param.id} companyId={companyForUser.id} basePath="/main/purchase/expense-report/view/" />}
                  </div>
                  <label className="fs-6 fw-bold mt-1">
                    {lang.labels.status}:
                    <span className={`badge badge-light-${colorStatusExpenseReport(report.status)} text-uppercase fs-7 ms-2`}>{statusDocumentValue(lang, report.status)}</span>
                    { report.status === 'validated' && report.payment_status &&
                      <span className={`badge badge-light-${colorStatusPayment(report.payment_status)} text-uppercase fs-7 ms-2`}>{paymentStatusValue(lang, report.payment_status)}</span>
                    }
                  </label>
                  { report.status === 'rejected' && report.reject_reason &&
                    <div className="alert alert-danger d-flex align-items-center p-3 mt-2 mb-0">
                      <span className="fw-bold">{lang.labels.rejectedReason}: </span>
                      <span className="ms-2">{report.reject_reason}</span>
                    </div>
                  }
                  { report.employee_bank_account?.iban &&
                    <div className="mt-1">
                      <span className="fw-bold text-muted">{lang.labels.iban}: </span>
                      <span>{report.employee_bank_account.iban}</span>
                    </div>
                  }
                </div>
                <div className="card-toolbar">
                  { (report.status === 'draft' || report.status === 'rejected') &&
                    <button className="btn btn-info mb-1 me-2" type="button" onClick={submitSubmit}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.43 8.56949L10.744 15.1395C10.6422 15.282 10.5804 15.4492 10.5651 15.6236C10.5498 15.7981 10.5815 15.9734 10.657 16.1315L13.194 21.4425C13.2737 21.6097 13.3991 21.751 13.5557 21.8499C13.7123 21.9488 13.8938 22.0014 14.079 22.0015H14.117C14.3087 21.9941 14.4941 21.9307 14.6502 21.8191C14.8062 21.7075 14.9261 21.5526 14.995 21.3735L21.933 3.33649C22.0011 3.15918 22.0164 2.96594 21.977 2.78013C21.9376 2.59432 21.8452 2.4239 21.711 2.28949L15.43 8.56949Z" fill="black"/>
                            <path opacity="0.3" d="M20.664 2.06648L2.62602 9.00148C2.44768 9.07085 2.29348 9.19082 2.1824 9.34663C2.07131 9.50244 2.00818 9.68731 2.00074 9.87853C1.99331 10.0697 2.04189 10.259 2.14054 10.4229C2.23919 10.5869 2.38359 10.7185 2.55601 10.8015L7.86601 13.3365C8.02383 13.4126 8.19925 13.4448 8.37382 13.4297C8.54839 13.4145 8.71565 13.3526 8.85801 13.2505L15.43 8.56548L21.711 2.28448C21.5762 2.15096 21.4055 2.05932 21.2198 2.02064C21.034 1.98196 20.8409 1.99788 20.664 2.06648Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.submitExpenseReport}
                      </div>
                    </button>
                  }
                  { report.status === 'submitted' &&
                    <>
                      <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={executeShowModalValidate}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black"></path>
                              <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15C5.1 15 4 16.1 4 17.5Z" fill="black"></path>
                            </svg>
                          </span>
                          {lang.labels.validate}
                        </div>
                      </button>
                      <button className="btn btn-danger mb-1 me-2" type="button" onClick={submitReject}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M12 10.6L14.8 7.8C15.2 7.4 15.8 7.4 16.2 7.8C16.6 8.2 16.6 8.80002 16.2 9.20002L13.4 12L12 10.6ZM10.6 12L7.8 14.8C7.4 15.2 7.4 15.8 7.8 16.2C8 16.4 8.3 16.5 8.5 16.5C8.7 16.5 8.99999 16.4 9.19999 16.2L12 13.4L10.6 12Z" fill="black"/>
                              <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12ZM13.4 12L16.2 9.20001C16.6 8.80001 16.6 8.19999 16.2 7.79999C15.8 7.39999 15.2 7.39999 14.8 7.79999L12 10.6L9.2 7.79999C8.8 7.39999 8.2 7.39999 7.8 7.79999C7.4 8.19999 7.4 8.80001 7.8 9.20001L10.6 12L7.8 14.8C7.4 15.2 7.4 15.8 7.8 16.2C8 16.4 8.3 16.5 8.5 16.5C8.7 16.5 9 16.4 9.2 16.2L12 13.4L14.8 16.2C15 16.4 15.3 16.5 15.5 16.5C15.7 16.5 16 16.4 16.2 16.2C16.6 15.8 16.6 15.2 16.2 14.8L13.4 12Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.rejectExpenseReport}
                        </div>
                      </button>
                    </>
                  }
                  { report.status === 'validated' &&
                    <>
                      <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={submitInvalidate}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black"></path>
                              <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15C5.1 15 4 16.1 4 17.5Z" fill="black"></path>
                            </svg>
                          </span>
                          {lang.labels.invalidate}
                        </div>
                      </button>
                      <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={executeShowModalCorrectAccounts}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                              <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                              <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.correctAccounts}
                        </div>
                      </button>
                    </>
                  }
                  { (report.status === "draft" || report.status === "rejected") &&
                    <Link to={"/main/purchase/expense-report/update/" + report.id} className="btn btn-primary mb-1 me-2">
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                            <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                            <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.edit}
                      </div>
                    </Link>
                  }
                  { report.status === 'draft' &&
                    <button className="btn btn-danger mb-1" type="button" onClick={submitDelete}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                            <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                            <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                          </svg>
                        </span>
                        {lang.labels.delete}
                      </div>
                    </button>
                  }
                </div>
              </div>
              <AppPurchaseViewDetail purchase={"expense"} record={report as any} employee={report.employee}></AppPurchaseViewDetail>
            </div>
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title"><h4>{lang.labels.expenses}</h4></div>
              </div>
              <div className="card-body pt-0">
                { expenses.filter(e => e !== null).length === 0
                  ?
                  <div className="text-center text-muted py-10">
                    <span className="fs-6">{lang.labels.noRecordsFound}</span>
                  </div>
                  :
                  <div className="table-responsive">
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3">
                      <thead>
                        <tr className="fw-bolder text-muted fs-7">
                          <th className="min-w-100px">{lang.labels.expenseNumber}</th>
                          <th className="min-w-120px">{lang.labels.merchant ?? lang.labels.provider}</th>
                          <th className="min-w-120px">{lang.labels.concerning}</th>
                          <th className="min-w-80px">{lang.labels.date}</th>
                          <th className="min-w-100px text-end">{lang.labels.amount}</th>
                          <th className="w-50px text-center">{lang.labels.receipt}</th>
                          <th className="w-50px text-center">{lang.labels.actions}</th>
                        </tr>
                      </thead>
                      <tbody>
                        { expenses.map((expense, index) => {
                          if (!expense) return null
                          const providerName = expense.provider ? formatContact(expense.provider) : '—'
                          const expTotal = expense.amount?.total ?? computeExpenseTotal(expense)
                          const foreignInfo = getExpenseForeignAmount(expense)
                          const currSymbol = companyForUser.currency?.symbol ?? companyForUser.currency?.code ?? ''

                          return (
                            <tr key={expense.id || index}>
                              <td><Link to={`/main/purchase/expense/view/${expense.id}`} className="fw-bolder text-dark text-hover-primary">{expense.expense_number || `#${index + 1}`}</Link></td>
                              <td className="fw-bold">{providerName}</td>
                              <td className="text-muted">{expense.concerning || '—'}</td>
                              <td className="text-muted">{expense.created_date ? dateFormat(lang.code, expense.created_date) : '—'}</td>
                              <td className="text-end">
                                <span className="fw-bolder text-dark">{formatMoney(expTotal)} {currSymbol}</span>
                                { foreignInfo &&
                                  <div className="text-muted fs-8">{formatMoney(foreignInfo.amount)} {foreignInfo.code}</div>
                                }
                              </td>
                              <td className="text-center">
                                { expense.vouchers && expense.vouchers.length > 0
                                  ? <ButtonVouchers vouchers={expense.vouchers}></ButtonVouchers>
                                  : <span className="text-muted">—</span>
                                }
                              </td>
                              <td className="text-center">
                                <Link to={`/main/purchase/expense/view/${expense.id}`} className="btn btn-icon btn-sm btn-light-primary">
                                  <span className="svg-icon svg-icon-5">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M21.7 18.9L18.6 15.8C17.9 16.9 16.9 17.9 15.8 18.6L18.9 21.7C19.3 22.1 19.9 22.1 20.3 21.7L21.7 20.3C22.1 19.9 22.1 19.3 21.7 18.9Z" fill="black"/>
                                      <path opacity="0.3" d="M11 20C6 20 2 16 2 11C2 6 6 2 11 2C16 2 20 6 20 11C20 16 16 20 11 20ZM11 4C7.1 4 4 7.1 4 11C4 14.9 7.1 18 11 18C14.9 18 18 14.9 18 11C18 7.1 14.9 4 11 4Z" fill="black"/>
                                    </svg>
                                  </span>
                                </Link>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={4}></td>
                          <td className="text-end">
                            <span className="fw-bolder fs-4 text-dark">{formatMoney(computeGrandTotal())} {companyForUser.currency?.symbol ?? companyForUser.currency?.code ?? ''}</span>
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                }
              </div>
            </div>
            { report.status === 'validated' &&
              <div className="card card-flush mb-5">
                <div className="card-header">
                  <div className="card-title"><h4>{lang.labels.payments}</h4></div>
                  <div className="card-toolbar">
                    { report.payment_status !== "fully-paid" &&
                      <button className="btn btn-sm btn-primary mb-1 me-2" type="button" onClick={executeShowModalPayment}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-3 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M3.20001 5.91897L16.9 3.01895C17.4 2.91895 18 3.219 18.1 3.819L19.2 9.01895L3.20001 5.91897Z" fill="black"/>
                              <path opacity="0.3" d="M13 13.9189C13 12.2189 14.3 10.9189 16 10.9189H21C21.6 10.9189 22 11.3189 22 11.9189V15.9189C22 16.5189 21.6 16.9189 21 16.9189H16C14.3 16.9189 13 15.6189 13 13.9189ZM16 12.4189C15.2 12.4189 14.5 13.1189 14.5 13.9189C14.5 14.7189 15.2 15.4189 16 15.4189C16.8 15.4189 17.5 14.7189 17.5 13.9189C17.5 13.1189 16.8 12.4189 16 12.4189Z" fill="black"/>
                              <path d="M13 13.9189C13 12.2189 14.3 10.9189 16 10.9189H21V7.91895C21 6.81895 20.1 5.91895 19 5.91895H3C2.4 5.91895 2 6.31895 2 6.91895V20.9189C2 21.5189 2.4 21.9189 3 21.9189H19C20.1 21.9189 21 21.0189 21 19.9189V16.9189H16C14.3 16.9189 13 15.6189 13 13.9189Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.reimburse}
                        </div>
                      </button>
                    }
                    { (report.payments ?? []).filter((p: any) => !p.is_deleted && !p.is_delete).length > 0 &&
                      <button className="btn btn-sm btn-primary mb-1" type="button" onClick={excuteViewOrHideButtonsToPaymentDelete}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-3 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M8.38 22H21C21.2652 22 21.5196 21.8947 21.7071 21.7072C21.8946 21.5196 22 21.2652 22 21C22 20.7348 21.8946 20.4804 21.7071 20.2928C21.5196 20.1053 21.2652 20 21 20H10L8.38 22Z" fill="black"/>
                              <path d="M15.622 15.6219L9.855 21.3879C9.66117 21.582 9.43101 21.7359 9.17766 21.8409C8.92431 21.946 8.65275 22 8.37849 22C8.10424 22 7.83268 21.946 7.57933 21.8409C7.32598 21.7359 7.09582 21.582 6.90199 21.3879L2.612 17.098C2.41797 16.9042 2.26404 16.674 2.15903 16.4207C2.05401 16.1673 1.99997 15.8957 1.99997 15.6215C1.99997 15.3472 2.05401 15.0757 2.15903 14.8224C2.26404 14.569 2.41797 14.3388 2.612 14.145L8.37801 8.37805L15.622 15.6219Z" fill="black"/>
                              <path opacity="0.3" d="M8.37801 8.37805L14.145 2.61206C14.3388 2.41803 14.569 2.26408 14.8223 2.15906C15.0757 2.05404 15.3473 2 15.6215 2C15.8958 2 16.1673 2.05404 16.4207 2.15906C16.674 2.26408 16.9042 2.41803 17.098 2.61206L21.388 6.90198C21.582 7.0958 21.736 7.326 21.841 7.57935C21.946 7.83269 22 8.10429 22 8.37854C22 8.65279 21.946 8.92426 21.841 9.17761C21.736 9.43096 21.582 9.66116 21.388 9.85498L15.622 15.6219L8.37801 8.37805Z" fill="black"/>
                            </svg>
                          </span>
                          {removePayment ? lang.labels.doNotDeletePayments : lang.labels.deleteSomePayment}
                        </div>
                      </button>
                    }
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="dataTables_wrapper">
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed gy-2 mb-2">
                        <thead className="border-bottom">
                          <tr className="text-muted text-uppercase fw-bolder fs-7">
                            <th className="w-25px text-nowrap text-start">{lang.labels.n}</th>
                            <th className="min-w-100px text-nowrap text-start">{lang.labels.createdDate}</th>
                            <th className="min-w-100px text-nowrap text-start">{lang.labels.sourceOfIncome}</th>
                            <th className="min-w-100px text-nowrap text-end">{lang.labels.amount} ({report.currency?.code})</th>
                            { removePayment && <th className="w-75px text-nowrap text-end">{lang.labels.remove}</th> }
                          </tr>
                        </thead>
                        { (report.payments ?? []).filter((p: any) => !p.is_deleted && !p.is_delete).length > 0
                          ?
                          <tbody className="text-secondary fw-bold">
                            { (report.payments ?? []).filter((p: any) => !p.is_deleted && !p.is_delete).map((item: any, index: number) => (
                              <tr key={index} className="row-dashed">
                                <td>{index + 1}</td>
                                <td>{dateFormat(lang.code, item.payment_date ?? item.created_date)}</td>
                                <td>{(item.method === 'credit-note') ? lang.labels.creditNote : treasuryAccountSelected(treasuryAccountsForCompany, item.source)}</td>
                                <td className="text-nowrap text-end">{formatMoney(item.amount)}</td>
                                { removePayment &&
                                  <td valign="middle" className="text-end p-0">
                                    <button className="btn btn-sm btn-icon btn-flex btn-bg-transparent btn-active-color-danger h-30px w-30px" type="button" onClick={() => submitPaymentDelete(item.id)}>
                                      <span className="svg-icon svg-icon-primary svg-icon-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                          <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                          <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                        </svg>
                                      </span>
                                    </button>
                                  </td>
                                }
                              </tr>
                            ))}
                          </tbody>
                          :
                          <tbody>
                            <tr className="text-secondary fw-bold">
                              <td colSpan={4} className="text-center">{lang.labels.thisExpenseReportHasNoPayments}</td>
                            </tr>
                          </tbody>
                        }
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            }
            <AppPurchaseViewAmount purchase={"expense"} record={report as any}></AppPurchaseViewAmount>
          </div>
          <AppPurchaseViewDraft record={report as any} vouchers={vouchers} asideMinimize={asideMinimize} setAsideMinimize={setAsideMinimize}></AppPurchaseViewDraft>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <ModalExpenseReportValidate companyForUser={companyForUser} settingsForCompany={settingsForCompany} accountingAccountsForCompany={accountingAccountsForCompany} accountingTaxAccountsForCompany={accountingTaxAccountsForCompany} accountingExpenseAccountsForCompany={accountingExpenseAccountsForCompany} report={report} setReport={setReport}></ModalExpenseReportValidate>
      <ModalExpenseReportValidate companyForUser={companyForUser} settingsForCompany={settingsForCompany} accountingAccountsForCompany={accountingAccountsForCompany} accountingTaxAccountsForCompany={accountingTaxAccountsForCompany} accountingExpenseAccountsForCompany={accountingExpenseAccountsForCompany} report={report} setReport={setReport} mode="correction"></ModalExpenseReportValidate>
      <ModalPaymentCreateExpenseReport companyForUser={companyForUser} accountingAccountsForCompany={accountingAccountsForCompany} accountingDebitEntriesForCompany={accountingDebitEntriesForCompany} treasuryAccountsForCompany={treasuryAccountsForCompany} report={report} setReport={setReport} loadAccountingDebitEntriesForCompany={loadAccountingDebitEntriesForCompany}></ModalPaymentCreateExpenseReport>
    </div>
  )
}

export default ExpenseReportViewPage;
